import { createClient } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { keepSessionWhenServerIsUnwell, giveUpOnASlowLogout, giveUpOnAHungRead, keepTheNewerSavedSession } from './utils/authFetch'
import { savedSessionKey, finishSignOutLeftUnfinished, signOutOf } from './utils/signOut'
import { lockThatNeverWaitsForEver } from './utils/authLock'
import { noteOncePerOutcome } from './utils/signInDiagnostic'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please copy .env.example to .env and fill in your credentials.'
  )
}

const SAVED_SESSION_KEY = savedSessionKey(supabaseUrl)

// Where the library keeps the session. Some browsers refuse even to hand the storage over.
const deviceStorage = (() => {
  try {
    return window.localStorage
  } catch {
    return null
  }
})()

// A "Log out" that was cut short (the app closed while the server was not answering) is finished
// here, before the client below reads the saved session. See src/utils/signOut.js.
finishSignOutLeftUnfinished(deviceStorage, SAVED_SESSION_KEY)

// The saved session now, which the joined Nutrition may have renewed (src/utils/authFetch.js)
const readSaved = () => JSON.parse(deviceStorage?.getItem(SAVED_SESSION_KEY) ?? 'null')
const keepingNewer = keepTheNewerSavedSession({
  readSaved,
  note: noteOncePerOutcome((message, context) => Sentry.captureMessage(message, context), 'fitness'),
})

// `fetch` is looked up at the time of each request, as the library itself does when given none.
export const supabase = createClient(supabaseUrl, supabaseKey, {
  global: { fetch: giveUpOnASlowLogout(keepSessionWhenServerIsUnwell(keepingNewer(giveUpOnAHungRead((input, init) => fetch(input, init))))) },
  // The sign-in lock never waits for ever (src/utils/authLock.js, 16 October 2026)
  auth: { lock: lockThatNeverWaitsForEver() },
})

/**
 * "Log out", for every screen that offers it. Resolves within a few seconds whether or not the
 * server answers, and the session is gone from this device either way.
 */
export const signOutOfThisDevice = () =>
  signOutOf({ client: supabase, storage: deviceStorage, key: SAVED_SESSION_KEY })
